/**
 * QuotePrintModal.tsx
 *
 * Pick a print template, see the quote as it will print, and download it.
 *
 * It prints `workingState` when given — the editor's copy, unsaved edits
 * included, as legacy's Print posts the on-screen form — and the saved quote
 * otherwise.
 *
 * A practice with quote templates must pick one first, as legacy requires
 * ("Please select a template."). A practice with none prints with the
 * standard layout, which is the one legacy gives a new template, rather than
 * being unable to print at all.
 *
 * The PDF arrives as bytes and is shown through an object URL minted fresh on
 * each render and revoked on unmount — a cached URL can already have been
 * revoked by the time it is reused, which is why payment receipts do the same.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, ExternalLink } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import {
  downloadQuotePdf,
  getQuotePrintTemplates,
  previewQuotePdf,
  type QuoteEditorState,
  type QuotePrintTemplate,
} from '@/services/quotes.service';

interface QuotePrintModalProps {
  quoteId: number | null;
  /** The editor's working copy. Omit to print the saved quote. */
  workingState?: QuoteEditorState;
  onClose: () => void;
}

export default function QuotePrintModal({ quoteId, workingState, onClose }: QuotePrintModalProps) {
  const open = quoteId !== null;
  const [templateId, setTemplateId] = useState<number | undefined>(undefined);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const {
    data: templates,
    isLoading: templatesLoading,
    isError: templatesError,
  } = useQuery({
    queryKey: ['quote-print-templates'],
    queryFn: getQuotePrintTemplates,
    enabled: open,
    staleTime: 300_000,
  });

  const hasTemplates = (templates ?? []).length > 0;
  const needsTemplate = hasTemplates && templateId === undefined;
  const ready = open && !templatesLoading && !templatesError && !needsTemplate;

  const {
    data: pdf,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['quote-pdf', quoteId, templateId, workingState ?? null],
    queryFn: () => previewQuotePdf(quoteId as number, templateId, workingState),
    enabled: ready,
  });

  // One object URL per blob, revoked when it is replaced or the modal closes.
  useEffect(() => {
    if (!pdf) {
      setObjectUrl(null);
      return;
    }

    const url = URL.createObjectURL(pdf);
    setObjectUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [pdf]);

  // A failed download belongs to that attempt, not to the next time the
  // window opens.
  useEffect(() => {
    if (!open) setDownloadError(null);
  }, [open]);

  async function handleDownload() {
    if (!quoteId) return;

    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await downloadQuotePdf(quoteId, templateId, workingState);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${quoteId}.pdf`;
      link.click();
      // Give the browser a moment to start the save before releasing the blob.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      // Fixed wording: the failure body arrives as a Blob, and there is no
      // server message in it a coordinator could act on.
      setDownloadError(
        'Could not download this quote. Try again. If it keeps happening, contact Prospera support.',
      );
    } finally {
      setDownloading(false);
    }
  }

  /**
   * Many phone browsers show nothing in an iframe pointed at a PDF, so the
   * preview can also open in its own tab (same fallback as LabPdfPreviewPanel).
   * The tab gets its own object URL: the preview's is revoked when this window
   * closes or the template changes, which would blank a tab still loading it.
   */
  function handleOpenInNewTab() {
    if (!pdf) return;

    const url = URL.createObjectURL(pdf);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Print Quote"
      size="xxl"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleOpenInNewTab}
            disabled={!objectUrl}
            leftIcon={<ExternalLink className="h-4 w-4" />}
          >
            Open in new tab
          </Button>
          <Button
            onClick={handleDownload}
            loading={downloading}
            disabled={!ready}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Download
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm text-slate-700 max-md:flex-wrap">
          <span className="font-medium">Template</span>
          <select
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm max-md:w-full"
            value={templateId ?? ''}
            disabled={!hasTemplates}
            onChange={(e) => {
              setDownloadError(null);
              setTemplateId(e.target.value ? Number(e.target.value) : undefined);
            }}
          >
            {hasTemplates ? (
              <option value="">Select one…</option>
            ) : (
              <option value="">Standard layout</option>
            )}
            {(templates ?? []).map((t: QuotePrintTemplate) => (
              <option key={t.templateId} value={t.templateId}>{t.name}</option>
            ))}
          </select>
        </label>

        {downloadError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {downloadError}
          </div>
        ) : null}

        {templatesError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Could not load the print templates. Close this window and open it again. If it keeps
            happening, contact Prospera support.
          </div>
        ) : needsTemplate ? (
          <p className="py-10 text-center text-sm text-slate-500">Choose a template to see the quote.</p>
        ) : templatesLoading || isLoading ? (
          <p className="py-10 text-center text-sm text-slate-500">Preparing the quote…</p>
        ) : isError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {hasTemplates
              ? 'Could not prepare this quote for printing. Try another template. If it keeps happening, contact Prospera support.'
              : 'Could not prepare this quote for printing. Close this window and open it again. If it keeps happening, contact Prospera support.'}
          </div>
        ) : objectUrl ? (
          <iframe title="Quote preview" src={objectUrl} className="h-[60vh] w-full rounded-md border border-slate-200" />
        ) : null}
      </div>
    </Modal>
  );
}
